/**
 * Auth Client
 * Uses ClearAuth endpoints: session, login, logout, register, request-magic-link
 */

export type User = {
  id: string;
  email: string;
  name?: string | null;
  createdAt: Date | string;
};

/** Shape of GET /api/auth/session response */
interface SessionResponse {
  user?: { id: string; email: string; name?: string | null; created_at?: string } | null;
}

/**
 * Get current authenticated user from session cookie
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetch('/api/auth/session', {
      credentials: 'include',
    });

    if (!response.ok) return null;

    const data = (await response.json()) as SessionResponse;
    const u = data.user;
    if (!u) return null;

    return {
      id: u.id,
      email: u.email,
      name: u.name ?? undefined,
      createdAt: (u as { created_at?: string }).created_at
        ? new Date((u as { created_at: string }).created_at)
        : new Date(),
    };
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<User> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 401) throw new Error('401 Unauthorized');
    if (status === 429) throw new Error('429 Too Many Requests');
    throw new Error(`Sign in failed with status ${status}`);
  }

  const data = (await response.json()) as { user: SessionResponse['user'] };
  const u = data.user!;
  return {
    id: u.id,
    email: u.email,
    name: u.name ?? undefined,
    createdAt: (u as { created_at?: string }).created_at
      ? new Date((u as { created_at: string }).created_at)
      : new Date(),
  };
}

/**
 * Sign out (clears session cookie)
 */
export async function signOut(): Promise<void> {
  await fetch('/api/auth/logout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  });
}

/**
 * Request a magic link to sign in (passwordless).
 * Sends an email with a link; user clicks to sign in.
 * Only works for existing users (register first if needed).
 */
export async function requestMagicLink(
  email: string,
  returnTo?: string
): Promise<void> {
  const response = await fetch('/api/auth/request-magic-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, returnTo: returnTo ?? '/' }),
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 429) throw new Error('429 Too Many Requests');
    throw new Error(`Request failed with status ${status}`);
  }

  // ClearAuth returns success even when user doesn't exist (email enumeration protection)
}

/**
 * Sign up with email and password
 */
export async function signUp(
  email: string,
  password: string,
  name?: string
): Promise<User> {
  const response = await fetch('/api/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, name }),
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 429) throw new Error('429 Too Many Requests');
    throw new Error(`Sign up failed with status ${status}`);
  }

  const data = (await response.json()) as { user: SessionResponse['user'] };
  const u = data.user!;
  return {
    id: u.id,
    email: u.email,
    name: u.name ?? undefined,
    createdAt: (u as { created_at?: string }).created_at
      ? new Date((u as { created_at: string }).created_at)
      : new Date(),
  };
}
